import React, { createContext, useContext, useState, useEffect } from "react";
import { Game } from "../types/ScheduleTypes";
import { useDepthChart } from "./DepthChartContext";
import { useClub } from "./ClubContext";
import { useSeason } from "./SeasonContext";
import { Side } from "../types/DepthChartTypes";
import { AllSideTabs } from "../constants";
import {
  GameLog,
  GameSituation,
  KickOffReturnPlayLog,
  KickoffReturnPlaySelection,
  OffensivePlayLog,
  OffensivePlaySelection,
  FieldGoalPlayLog,
  FieldGoalBlockPlayLog,
  FieldGoalPlaySelection,
  KickoffPlayLog,
  KickOffPlaySelection,
  DefensivePlayLog,
  DefensivePlaySelection,
  PuntPlayLog,
  PuntPlaySelection,
  PuntReturnPlayLog,
  PuntReturnPlaySelection,
  Penalty,
  PenaltyLog,
  SeasonGameLog,
} from "../types/LogTypes";
import axios from "axios";
import { API_BASE_URL } from "../constants";

interface LoggerContextType {
  selectedGame: Game | null;
  selectedTab: number;
  selectedSide: string;
  gameStarted: boolean;
  gameLog: GameLog | null;
  gameLogs: SeasonGameLog[] | null;
  currentPlayCall:
    | OffensivePlaySelection
    | KickoffReturnPlaySelection
    | FieldGoalPlaySelection
    | KickOffPlaySelection
    | DefensivePlaySelection
    | PuntPlaySelection
    | PuntReturnPlaySelection
    | null;
  currentPlayLog:
    | OffensivePlayLog
    | KickOffReturnPlayLog
    | FieldGoalPlayLog
    | FieldGoalBlockPlayLog
    | KickoffPlayLog
    | DefensivePlayLog
    | PuntPlayLog
    | PuntReturnPlayLog
    | null;
  currentPlayPenalty: PenaltyLog | null;
  gameSituation: GameSituation;
  touchdown: boolean;
  safety: boolean;
  penalties: Penalty[];
  setSelectedGame: (game: Game | null) => void;
  setSelectedTab: (tab: number) => void;
  setSelectedSide: (side: Side) => void;
  setGameStarted: (gameStarted: boolean) => void;
  onStartGame: (startSide: "KICKOFF" | "KICKOFF_RETURN") => void;
  setGameLog: (gameLog: GameLog | null) => void;
  setCurrentPlayCall: (
    playCall:
      | OffensivePlaySelection
      | KickoffReturnPlaySelection
      | FieldGoalPlaySelection
      | KickOffPlaySelection
      | DefensivePlaySelection
      | PuntPlaySelection
      | PuntReturnPlaySelection
      | null
  ) => void;
  setCurrentPlayLog: (
    playLog:
      | OffensivePlayLog
      | KickOffReturnPlayLog
      | FieldGoalPlayLog
      | FieldGoalBlockPlayLog
      | KickoffPlayLog
      | DefensivePlayLog
      | PuntPlayLog
      | PuntReturnPlayLog
      | null
  ) => void;
  setCurrentPlayPenalty: (penalty: PenaltyLog) => void;
  onSaveLog: (
    playType: Side,
    playCall:
      | OffensivePlaySelection
      | KickoffReturnPlaySelection
      | FieldGoalPlaySelection
      | KickOffPlaySelection
      | DefensivePlaySelection
      | PuntPlaySelection
      | PuntReturnPlaySelection,
    playLog:
      | OffensivePlayLog
      | KickOffReturnPlayLog
      | FieldGoalPlayLog
      | FieldGoalBlockPlayLog
      | KickoffPlayLog
      | DefensivePlayLog
      | PuntPlayLog
      | PuntReturnPlayLog
  ) => void;
  onSaveCurrentPlay: () => void;
  deleteGameLog: () => void;
  deleteLog: (logId: string) => Promise<void>;
  setGameSituation: (gameSituation: GameSituation) => void;
  setTouchdown: (touchdown: boolean) => void;
  setSafety: (safety: boolean) => void;
  setPenalties: (penalties: Penalty[]) => void;
  clearSelections: boolean;
  setClearSelections: (clearSelections: boolean) => void;
  setGameLogs: (gameLogs: SeasonGameLog[]) => void;
  fetchGameLogs: () => void;
  exportLogs: (gameLogId: number) => Promise<any>;
}

export const LoggerContext = createContext<LoggerContextType | undefined>(
  undefined
);

export const LoggerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedSeason } = useSeason();
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedSide, setSelectedSide] = useState<Side>("OFFENSE");
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [gameLog, setGameLog] = useState<GameLog | null>(null);
  const [gameLogs, setGameLogs] = useState<SeasonGameLog[] | null>(null);
  const [currentPlayCall, setCurrentPlayCall] = useState<
    | OffensivePlaySelection
    | KickoffReturnPlaySelection
    | FieldGoalPlaySelection
    | KickOffPlaySelection
    | DefensivePlaySelection
    | PuntPlaySelection
    | PuntReturnPlaySelection
    | null
  >(null);
  const [currentPlayLog, setCurrentPlayLog] = useState<
    | OffensivePlayLog
    | KickOffReturnPlayLog
    | FieldGoalPlayLog
    | FieldGoalBlockPlayLog
    | KickoffPlayLog
    | DefensivePlayLog
    | PuntPlayLog
    | PuntReturnPlayLog
    | null
  >(null);
  const [currentPlayPenalty, setCurrentPlayPenalty] =
    useState<PenaltyLog | null>(null);
  const [touchdown, setTouchdown] = useState<boolean>(false);
  const [safety, setSafety] = useState<boolean>(false);
  const [penalties, setPenalties] = useState<Penalty[]>([]);
  const [clearSelections, setClearSelections] = useState<boolean>(false);
  const [gameSituation, setGameSituation] = useState<GameSituation>({
    down: -1,
    distance: -1,
    yardline: 50,
    quarter: 0,
    time_remaining: "12:00",
    club_score: 0,
    opp_score: 0,
    club_tol: 3,
    opp_tol: 3,
    club_possession: true,
  });
  const { getDepthChartsForGame } = useDepthChart();

  // Check for existing game log when game is selected but not started
  // This could be because of a refresh - we want to restore the game log
  // and the game started state
  useEffect(() => {
    const checkExistingGameLog = async () => {
      console.log(
        "checking for existing game log",
        selectedGame?.id,
        gameStarted
      );
      if (selectedGame?.id && !gameStarted) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/game-logs/get_game_log/`,
            {
              params: {
                game_id: selectedGame.id,
              },
            }
          );
          if (response.data) {
            console.log("found existing game log", response.data);
            // Found existing game log - restore state
            setGameLog(response.data);
            setGameStarted(true);

            const lastPlay = response.data.logs[response.data.logs.length - 1];
            if (lastPlay) {
              setGameSituation(lastPlay.game_situation);
            }
            getDepthChartsForGame(selectedGame.id);

            console.log("Restored existing game log:", response.data);
          }
        } catch (error) {
          // No existing game log found or error occurred
          console.log("No existing game log found for this game");
        }
      }
    };

    checkExistingGameLog();
  }, [selectedGame, gameStarted]);

  // get penalties
  useEffect(() => {
    fetchPenalties();
  }, []);

  const createGameLog = async () => {
    if (selectedGame?.id) {
      console.log("Creating game log");
      const response = await axios.post(`${API_BASE_URL}/game-logs/`, {
        game_id: selectedGame.id,
      });
      setGameLog(response.data);
    }
  };

  const deleteGameLog = async () => {
    if (gameLog?.id) {
      const response = await axios.delete(
        `${API_BASE_URL}/game-logs/${gameLog.id}/`
      );
      setGameLog(null);
      setGameStarted(false);
    }
  };

  const deleteLog = async (logId: string) => {
    try {
      await axios.delete(`${API_BASE_URL}/logs/${logId}/`);
      await fetchGameLog(); // Refresh the game log after deletion
    } catch (error) {
      console.error("Error deleting log:", error);
      throw error; // Let the component handle the error
    }
  };

  const onStartGame = (startSide: "KICKOFF" | "KICKOFF_RETURN") => {
    if (selectedGame?.id) {
      getDepthChartsForGame(selectedGame.id);
    }
    const updatedGameSituation = {
      ...gameSituation,
      club_possession: startSide === "KICKOFF",
      yardline: 40,
      quarter: 1,
    };
    setGameSituation(updatedGameSituation);
    createGameLog();
    setGameStarted(true);
    setSelectedSide(startSide);
    setSelectedTab(AllSideTabs.findIndex((tab) => tab.value === startSide));
  };

  const onSaveLog = async (
    playType: Side,
    playCall:
      | OffensivePlaySelection
      | KickoffReturnPlaySelection
      | FieldGoalPlaySelection
      | KickOffPlaySelection
      | DefensivePlaySelection
      | PuntPlaySelection
      | PuntReturnPlaySelection,
    playLog:
      | OffensivePlayLog
      | KickOffReturnPlayLog
      | FieldGoalPlayLog
      | FieldGoalBlockPlayLog
      | KickoffPlayLog
      | DefensivePlayLog
      | PuntPlayLog
      | PuntReturnPlayLog,
    penaltyLog?: PenaltyLog | null
  ) => {
    console.log("onSaveLog", playType, playCall, playLog, penaltyLog);
    if (gameLog) {
      const response = await axios.post(`${API_BASE_URL}/logs/`, {
        game_log_id: gameLog.id,
        play_type: playType,
        play_call: playCall,
        play_log: playLog,
        game_situation: gameSituation,
        touchdown: touchdown,
        safety: safety,
        penalty_log: penaltyLog,
      });
      console.log("response", response.data);
      setCurrentPlayCall(null);
      setCurrentPlayLog(null);
      setCurrentPlayPenalty(null);
      setTouchdown(false);
      setSafety(false);
      setGameSituation(response.data.game_situation);
      setSelectedSide(response.data.selected_side);
      setSelectedTab(
        AllSideTabs.findIndex(
          (tab) => tab.value === response.data.selected_side
        )
      );
      setClearSelections(false);
      fetchGameLog();
    }
  };

  const onSaveCurrentPlay = () => {
    if (currentPlayCall && currentPlayLog) {
      setClearSelections(true);
      onSaveLog(
        selectedSide as Side,
        currentPlayCall,
        currentPlayLog,
        currentPlayPenalty
      );
    }
  };
  // Specific Log for a game
  const fetchGameLog = async () => {
    if (selectedGame?.id) {
      const response = await axios.get(
        `${API_BASE_URL}/game-logs/get_game_log/`,
        {
          params: { game_id: selectedGame.id },
        }
      );
      setGameLog(response.data);
    }
  };
  // All Logs for the season
  const fetchGameLogs = async () => {
    if (selectedSeason?.id) {
      const response = await axios.get(
        `${API_BASE_URL}/game-logs/get_season_game_log/`,
        {
          params: {
            season_id: selectedSeason?.id,
          },
        }
      );
      setGameLogs(response.data);
    }
  };

  const fetchPenalties = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/penalties/`);
      setPenalties(response.data);
    } catch (error) {
      console.error("Error fetching penalties:", error);
    }
  };

  const exportLogs = async (gameLogId: number) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/game-logs/export_logs/`,
        {
          game_log_id: gameLogId,
        },
        {
          responseType: 'blob', // Important: tells axios to handle response as binary data
        }
      );
      
      // Create a blob from the response data
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = `game_log_${gameLogId}.xlsx`; // Set the download filename
      
      // Append link to body, click it, and remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error("Error exporting logs:", error);
      return false;
    }
  };

  return (
    <LoggerContext.Provider
      value={{
        selectedGame,
        selectedTab,
        selectedSide,
        gameStarted,
        gameLog,
        gameLogs,
        currentPlayCall,
        currentPlayLog,
        gameSituation,
        touchdown,
        safety,
        penalties,
        currentPlayPenalty,
        clearSelections,
        setCurrentPlayPenalty,
        setTouchdown,
        setSafety,
        setSelectedGame,
        setSelectedTab,
        setSelectedSide,
        setGameStarted,
        onStartGame,
        setGameLog,
        setCurrentPlayCall,
        setCurrentPlayLog,
        onSaveLog,
        onSaveCurrentPlay,
        deleteGameLog,
        deleteLog,
        setGameSituation,
        setPenalties,
        setClearSelections,
        setGameLogs,
        fetchGameLogs,
        exportLogs,
      }}
    >
      {children}
    </LoggerContext.Provider>
  );
};

export const useLogger = () => {
  const context = useContext(LoggerContext);
  if (context === undefined) {
    throw new Error("useLogger must be used within a LoggerProvider");
  }
  return context;
};
